// 引入我们刚刚创建的global.js api文件
import global from '@/api/global.js'

import {
  Message
} from 'element-ui'
import { jumpTerminusWithStore } from '@/plugins/mixins'
import { getAcceptLanguage, normalizeLocale } from '@/utils/locale'
import { getMallCountry, getMallStation, hydrateMallSiteFromCookies, readStoredMallSite } from '@/utils/mallSite'

/** 商品请求的站点头。服务端只读当前请求 Cookie，不读模块缓存 */
function resolveMallRequestHeaders(cookies) {
  if (process.server) {
    const stored = readStoredMallSite(cookies)
    return {
      country: stored && stored.country ? stored.country : '',
      station: stored && stored.station ? stored.station : '',
    }
  }

  hydrateMallSiteFromCookies(cookies)
  return {
    country: getMallCountry(),
    station: getMallStation(),
  }
}

function setupXCoinsToast() {
  if (!process.client || window.__xCoinsToastReady) {
    return
  }

  window.__xCoinsToastReady = true
  window.__xCoinsLastClickTarget = null
  window.__xCoinsLastToast = null

  const style = document.createElement('style')
  style.textContent = `
    .xcoins-point-toast {
      position: fixed;
      z-index: 3000;
      padding: 7px 10px;
      border-radius: 999px;
      background: rgba(28, 32, 36, 0.92);
      color: #fff;
      font-size: 13px;
      font-weight: 700;
      line-height: 18px;
      pointer-events: none;
      white-space: nowrap;
      box-shadow: 0 6px 18px rgba(0, 0, 0, 0.16);
      animation: xcoins-point-toast-fade 1.8s ease forwards;
    }

    @keyframes xcoins-point-toast-fade {
      0% {
        opacity: 0;
        transform: translate(0, 8px);
      }
      16% {
        opacity: 1;
        transform: translate(0, 0);
      }
      72% {
        opacity: 1;
        transform: translate(0, -4px);
      }
      100% {
        opacity: 0;
        transform: translate(0, -12px);
      }
    }
  `
  document.head.appendChild(style)

  document.addEventListener('click', (event) => {
    const target = event.target
    if (!target || !target.closest) {
      return
    }

    window.__xCoinsLastClickTarget = target.closest(
      'button, a, [role="button"], .side-btn, .operate-btn, .cw-btn, .post-btn, .upload-btn, .upload-btn-box, .nav-list, .comment-like, .shar-item, .cp-btn, [class*="btn"]'
    ) || target
  }, true)
}

function getXCoinsToastTarget(targetElement) {
  if (!targetElement) {
    return window.__xCoinsLastClickTarget
  }

  if (targetElement.currentTarget) {
    return targetElement.currentTarget
  }

  if (targetElement.target && targetElement.target.closest) {
    return targetElement.target.closest(
      'button, a, [role="button"], .side-btn, .operate-btn, .cw-btn, .post-btn, .upload-btn, .upload-btn-box, .nav-list, .comment-like, .shar-item, .cp-btn, [class*="btn"]'
    ) || targetElement.target
  }

  return targetElement
}

function showXCoinsToast(point, targetElement) {
  if (!process.client) {
    return
  }

  const number = Number(String(point).replace(/,/g, ''))
  if (!Number.isFinite(number) || number <= 0) {
    return
  }

  setupXCoinsToast()

  const now = Date.now()
  const lastToast = window.__xCoinsLastToast
  if (lastToast && lastToast.point === number && now - lastToast.time < 500) {
    return
  }
  window.__xCoinsLastToast = { point: number, time: now }

  const target = getXCoinsToastTarget(targetElement)
  const rect = target && target.getBoundingClientRect
    ? target.getBoundingClientRect()
    : {
      top: window.innerHeight / 2,
      right: window.innerWidth / 2,
      width: 0,
    }

  const toast = document.createElement('div')
  toast.className = 'xcoins-point-toast'
  toast.textContent = `+${number.toLocaleString('en-US')} X-Coins`
  document.body.appendChild(toast)

  const toastWidth = toast.offsetWidth
  const left = Math.min(
    Math.max(rect.right - toastWidth / 2, 8),
    window.innerWidth - toastWidth - 8
  )
  const top = Math.max(rect.top - 12, 8)

  toast.style.left = `${left}px`
  toast.style.top = `${top}px`

  window.setTimeout(() => {
    if (toast.parentNode) {
      toast.parentNode.removeChild(toast)
    }
  }, 1800)
}

export default function ({
  app,
  $axios,
  store,
  redirect,
  req

}, inject) {

  setupXCoinsToast()
  inject('showXCoinsToast', showXCoinsToast)

  function axiosConfig($axios) {
    // 设置API的域名
    $axios.setBaseURL(process.server ? process.env.NODE_ENV == 'development' ? 'https://dev.xfans.xp-pen.com' : 'https://apixfans.xp-pen.com' : process.env.NODE_ENV == 'development' ? '' : '')
    $axios.defaults.timeout = 300000
    // 设置请求拦截
    $axios.defaults.withCredentials = true
    $axios.onRequest((config) => {
      config.headers = config.headers || {}
      config.headers['Content-Type'] = 'application/json; charset=utf-8'
      const locale = normalizeLocale(
        (app && app.i18n && app.i18n.locale) || (store && store.state && store.state.locale)
      )
      if (process.client) {
        config.__xCoinsToastTarget = window.__xCoinsLastClickTarget || null
      }
      if (process.server && req && req.headers && req.headers.cookie) {
        config.headers.cookie = req.headers.cookie
      }
      const isMainSite = config.is_main_site || false
      if (isMainSite) {
        // 主站接口改用 params.lang 切换语种（与 footer code 一致），不再使用 Accept-Language
        delete config.headers['accept-language']
        delete config.headers['Accept-Language']
        config.params = {
          ...(config.params || {}),
          lang: locale,
        }
        if (process.env.NODE_ENV === 'development') {
          config.baseURL = 'https://sso.xp-pen.com'
        } else if (process.env.NODE_ENV === 'production') {
          config.baseURL = 'https://account.xp-pen.com'
        }
      } else {
        config.headers['accept-language'] = getAcceptLanguage(locale)
      }
      if (String(config.url || '').includes('/mallPoints/api/goods')) {
        const mallSite = resolveMallRequestHeaders(app && app.$cookies)
        if (mallSite.country) {
          config.headers.country = mallSite.country
        }
        if (mallSite.station) {
          config.headers.station = mallSite.station
        }
      }
      if (process.server) {
        let logs = require('./logs.js');
        logs(`before request process.server:${config.url}${config.baseURL}  Date:${new Date()}`);
        logs = null;
      }
      // if (process.client) {
      //   let logs = require('./logs.js');
      //   logs(`before request process.client:${config.url}${config.baseURL}  Date:${new Date()}`);
      //   logs = null;
      // }
      return config
    })
    // 设置响应拦截
    $axios.onResponse((response) => {

      const status = response.status
      if (+status === 200) {

        if (response.data && (response.data.code == 200 || response.data.code == '200')) {
          showXCoinsToast(
            response.data.data && response.data.data.point,
            response.config && response.config.__xCoinsToastTarget
          )
        }

        if (response.data.code == '406' && process.client) {
          store.commit('SIGNOUT')
          Message({
            message: 'Please log in to operate',
            type: 'warning'
          });
          setTimeout(() => {
            jumpTerminusWithStore(store, 1)
          }, 2000)

        }

        // 返回接口数据
        return response.data
      } else {



        const responseConfig = response ? response.config : {}

      }
    })

    // axios错误处理
    $axios.onError((error) => {
      const response = error.response || {}
      const responseConfig = response.config || {}
      if (process.server) {
        let logs = require('./logs.js');
        logs(`process.server response Error:${response}  Date:${new Date()} `);
        logs = null;
      }
      // if (process.client) {
      //   let logs = require('./logs.js');
      //   logs(`process.client response Error:${response}  Date:${new Date()} `);
      //   logs = null;
      // }
    })
    //最后返回$axios对象
    return $axios
  }


  // 最后将我们的api文件进行注入
  inject('global', global(axiosConfig($axios.create())))
}
